/* global trapFocus, removeTrapFocus */

/**
 * <coupons-modal>
 * A coupons list dialog (centered modal on desktop, bottom sheet on mobile).
 * Opened via the document-level `dispatch:coupons:open` event fired by the
 * `.js-open-coupons` trigger. Applies / removes discount codes through
 * /cart/update.js and triggers a full cart re-render.
 *
 * State-machine notes (per requirements):
 * - Before opening, collapse any open <cart-summary-accordion> so the two
 *   bottom panels never stack.
 * - While open: backdrop + body scroll lock + focus trap.
 * - After close: do NOT re-open the cart summary (keep the page clean).
 */
if (!customElements.get('coupons-modal')) {
  class CouponsModal extends HTMLElement {
    appliedCode = ''
    shouldUpdateCart = false
    constructor() {
      super();

      this.context = this.dataset.context || 'drawer';
      this.panel = this.querySelector('.coupons-modal__panel');
      this.backdrop = this.querySelector('.coupons-modal__backdrop');
      this.errorEl = this.querySelector('.js-coupons-error');

      this.openHandler = this.handleOpenEvent.bind(this);
      this.clickHandler = this.handleClick.bind(this);
      this.keyupHandler = (evt) => evt.key === 'Escape' && this.close();
    }

    connectedCallback () {
      // Only the modal matching the currently-relevant context should react to the
      // open event. Both the drawer and the /cart page can render one; the trigger
      // that fired the event lives in the same `.coupons` wrapper.
      document.addEventListener('dispatch:coupons:open', this.openHandler);
      this.addEventListener('click', this.clickHandler);

      // Remember where we live so close() can move us back. We must move the
      // modal out to <body> on open, otherwise ancestor transforms (notably
      // <cart-drawer>'s `transform: translate3d(...)` even in the open state)
      // create a containing block for our `position: fixed` descendants and
      // the modal/panel/backdrop end up clipped to the drawer instead of the
      // viewport. We restore the original parent on close so the next section
      // re-render (which rebuilds .cart-drawer__summary / .cart__summary)
      // still swaps out this node like any other child.
      this.originalParent = this.parentNode;
      this.originalNextSibling = this.nextSibling;

      // De-dup: when a section re-render replaces .cart-drawer__summary, the
      // OLD <coupons-modal> survives on <body> (we teleported it there in
      // open() to escape the ancestor containing block). It still has a
      // `dispatch:coupons:open` listener, so the next click on .js-open-coupons
      // would open BOTH the old (orphan) and the new modal at once. Remove any
      // orphan instances for the same context right now — this node is the
      // authoritative replacement.
      Array.from(document.querySelectorAll('coupons-modal'))
        .filter((el) => el !== this && el.dataset.context === this.context && document.body.contains(el))
        .forEach((el) => el.remove());

      // Manual code entry — plain <div data-coupons-manual>, NOT a <form>. We
      // rely entirely on the modal-level `handleClick` (delegated, bound once
      // via `this.clickHandler` in the constructor) to route clicks on the
      // manual submit button — no separate listener on the button itself, so
      // there is no double-trigger to guard against. We only need to listen
      // for Enter on the input to mirror native form-submit behaviour.
      this.manualForm = this.querySelector('[data-coupons-manual]');
      if (this.manualForm) {
        this.input = this.manualForm.querySelector('.js-coupon-input');
        this.manualSubmitBtn = this.manualForm.querySelector('.js-apply-manual-coupon');

        this.appliedCode = (this.input.value || '').trim();
        this.updateManualButtonState();
        this.input.addEventListener('keydown', (evt) => {
          if (evt.key === 'Enter') {
            evt.preventDefault();
            this.manualSubmitBtn.click();
          }
        });
        this.input.addEventListener('input', (evt) => {
          this.updateManualButtonState();
        })
      }
    }
    normalizeCode (value) {
      return (value || '').trim().toLowerCase();
    }

    updateManualButtonState () {
      if (!this.manualSubmitBtn || !this.input) return;

      const inputValue = this.normalizeCode(this.input.value);
      const appliedValue = this.normalizeCode(this.appliedCode);

      const shouldCancel = Boolean(appliedValue) && inputValue === appliedValue;

      this.manualSubmitBtn.dataset.mode = shouldCancel ? 'cancel' : 'apply';
      this.manualSubmitBtn.textContent = shouldCancel
        ? (this.getCouponString('cancel') || 'Cancel')
        : (this.getCouponString('apply') || 'Apply');
    }

    getCouponString (key) {
      return (theme && theme.strings && theme.strings.coupons && theme.strings.coupons[key]) || '';
    }

    disconnectedCallback () {
      document.removeEventListener('dispatch:coupons:open', this.openHandler);
      this.unlockScroll();
    }

    /**
     * Handles the document-level open event; ignores it unless it targets this
     * modal's context.
     * @param {CustomEvent} evt
     */
    handleOpenEvent (evt) {
      if (evt.detail && evt.detail.context && evt.detail.context !== this.context) return;
      this.open(evt.detail ? evt.detail.opener : null);
    }

    /**
     * Handles clicks inside the modal (close buttons, apply, remove).
     * @param {object} evt - Event object.
     */
    handleClick (evt) {
      if (evt.target.closest('.js-close-coupons')) {
        this.close();
        return;
      }

      // Manual submit (Apply / Cancel). The button lives inside <div
      // data-coupons-manual> with the input. We use the input's value as the
      // payload, or '' (Remove) when in Cancel mode.
      const manualBtn = evt.target.closest('.js-apply-manual-coupon');
      if (manualBtn) {
        const code = (this.input && this.input.value || '').trim();
        if (!code) {
          this.clearError();
          return;
        }
        const payload = manualBtn.dataset.mode === 'cancel' ? '' : code;
        this.applyDiscount(payload, manualBtn);
        return;
      }

      const applyBtn = evt.target.closest('.js-apply-coupon');
      if (applyBtn) {
        this.applyDiscount(applyBtn.dataset.code, applyBtn);
        return;
      }

      const removeBtn = evt.target.closest('.js-remove-coupon');
      if (removeBtn) {
        this.applyDiscount('', removeBtn);
      }
    }

    /**
     * Opens the modal. Collapses the cart summary accordion first.
     * @param {Element} [opener] - Element that triggered opening.
     */
    open (opener) {
      this.opener = opener;


      // 1a. Teleport to <body> so our `position: fixed` panel + backdrop use
      // the viewport as the containing block. If the modal is currently
      // re-attached to its original slot after a previous close, the
      // originalParent reference is still valid (DOM nodes are the same).
      if (this.parentNode !== document.body) {
        // Refresh the original-parent reference in case the section was
        // re-rendered while we were open. (When close() restores us and the
        // section then re-renders, our originalParent may have been removed
        // from the tree; in that case the new node is a *different* element
        // and this code path is unreachable, which is fine — there's
        // nothing to restore to.)
        this.originalParent = this.parentNode;
        this.originalNextSibling = this.nextSibling;
        document.body.appendChild(this);
      }

      // 2. Reveal + lock scroll + trap focus.
      this.hidden = false;
      this.setAttribute('aria-hidden', 'false');
      // Force reflow so the CSS transition runs from the hidden state.
      this.offsetHeight; // eslint-disable-line no-unused-expressions
      this.classList.add('is-open');

      this.lockScroll();
      trapFocus(this, this.querySelector('.coupons-modal__close'));

      document.addEventListener('keyup', this.keyupHandler);
    }

    /**
     * Closes the modal. Does NOT restore the cart summary state.
     */
    close () {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      this.unlockScroll();
      removeTrapFocus(this.opener);
      document.removeEventListener('keyup', this.keyupHandler);
      this.clearError();
      if (this.shouldUpdateCart) this.refreshCart()

      const onEnd = (evt) => {
        if (evt.target !== this.panel) return;
        this.hidden = true;
        // Move back to the original parent so the next section re-render
        // rebuilds this node like any other child. Skip silently if the
        // original parent is no longer in the document (the caller has
        // already re-rendered and our node is on its way out anyway).
        this.restoreToOrigin();
        this.panel.removeEventListener('transitionend', onEnd);
      };
      this.panel.addEventListener('transitionend', onEnd);
      // Fallback in case no transition fires (reduced motion).
      setTimeout(() => {
        if (!this.classList.contains('is-open')) {
          this.hidden = true;
          this.restoreToOrigin();
        }
      }, 400);
    }

    /**
     * Re-attaches the modal to the parent/position it had before open() teleported
     * it to <body>. No-op if it's already there, or if the original slot is gone.
     */
    restoreToOrigin () {
      if (this.parentNode === document.body && this.originalParent && document.body.contains(this.originalParent)) {
        this.originalParent.insertBefore(this, this.originalNextSibling);
      }
    }

    /**
     * Applies (or, with an empty code, removes) a discount code via
     * /cart/update.js and re-renders the cart. Shopify's `discount` field has
     * replace semantics, so switching mutually-exclusive coupons needs no
     * explicit removal.
     * @param {string} code - Discount code, or '' to clear.
     * @param {HTMLButtonElement} button - The clicked button.
     */
    async applyDiscount (code, button) {
      this.clearError();
      button.classList.add('is-loading');
      button.disabled = true;

      try {
        const response = await fetch(`${theme.routes.cartUpdate}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({ discount: code })
        });

        const cart = await response.json();
        this.shouldUpdateCart = true

        // Empty code = Remove. This is an explicit user intent (Cancel button
        // or .js-remove-coupon click), never an invalid-code error — skip the
        // "did it take effect" check entirely.
        if (!code) {
          this.appliedCode = '';
          this.updateManualButtonState();
          document.querySelector('coupons-list')?.update("");
          this.close()
          return;
        }

        // For Apply, validate that the code actually took effect. If Shopify
        // silently ignored an invalid/ineligible code, the discount list won't
        // contain it — surface an error.
        const codeDown = code.toLowerCase();
        const applied = (cart.cart_level_discount_applications || []).some(
          (d) => (d.discount_application.title || '').toLowerCase() === codeDown
        );
        const applicable = (cart.discount_codes || []).some(d => d.applicable && (d.code || "").toLowerCase() === codeDown)
        console.log(applicable, '===', cart.discount_codes)
        if (!applied && !applicable) {
          this.showError(theme.strings.coupons ? theme.strings.coupons.invalid : '');
          this.updateManualButtonState();
          document.querySelector('coupons-list')?.update("");
          return;
        }
        this.appliedCode = code;
        this.updateManualButtonState();
        document.querySelector('coupons-list')?.update(code);
        // Don't clear the input here — the modal will close and re-render
        // anyway, and the Liquid-rendered `value` attribute will reflect the
        // newly-applied code on the next open. If we ever stop closing the
        // modal on apply, the input will still show the current code.
        this.close()
      } catch (error) {
        this.showError((error && error.message) || (theme.strings.coupons && theme.strings.coupons.error) || '');
      } finally {
        button.classList.remove('is-loading');
        button.disabled = false;
      }
    }

    /**
     * Triggers a full cart re-render for the current context. The summary
     * container (which holds this snippet) is rebuilt, so trigger state and
     * coupon-card states re-render from Liquid automatically.
     */
    refreshCart () {
      if (this.context === 'drawer') {
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', { bubbles: true }));
      } else {
        const cartItems = document.getElementById('cart-items') || document.querySelector('cart-items');
        if (cartItems && typeof cartItems.refresh === 'function') {
          cartItems.refresh();
        } else {
          window.location.reload();
        }
      }
    }

    showError (message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || (theme.strings.coupons && theme.strings.coupons.error) || '';
      this.errorEl.hidden = false;
    }

    clearError () {
      if (!this.errorEl) return;
      this.errorEl.hidden = true;
      this.errorEl.textContent = '';
    }

    lockScroll () {
      this.scrollY = window.scrollY;
      document.body.classList.add('fixed');
      document.body.style.top = `-${this.scrollY}px`;
      document.documentElement.style.height = '100vh';
    }

    unlockScroll () {
      if (!document.body.classList.contains('fixed')) return;
      document.documentElement.style.height = '';
      document.body.classList.remove('fixed');
      document.body.style.top = '';
      window.scrollTo(0, this.scrollY || 0);
    }
  }

  customElements.define('coupons-modal', CouponsModal);
}

/**
 * Delegated trigger handler. The trigger button lives inside the cart summary
 * container which is re-rendered on cart updates, so we delegate from document
 * rather than binding per-instance.
 */
if (!window.__couponsTriggerBound) {
  window.__couponsTriggerBound = true;
  document.addEventListener('click', (evt) => {
    const trigger = evt.target.closest('.js-open-coupons');
    if (!trigger) return;
    evt.preventDefault();
    evt.stopPropagation();

    const wrapper = trigger.closest('.coupons');
    const context = wrapper ? wrapper.dataset.context : 'drawer';
    document.dispatchEvent(
      new CustomEvent('dispatch:coupons:open', {
        detail: { opener: trigger, context }
      })
    );
  });
}
